import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';
import { onSocket } from '../utils/socket';

const idOf = (v) => String(v?._id || v || '');

/**
 * How many tasks are open between me and the other person in a direct chat,
 * for the badge on the chat header's Tasks button.
 *
 * Counts only - the panel pages through the tasks themselves. `personId` null
 * (a group, or no chat open) means no request. The panel hands back the counts
 * of every page it loads through setCounts, so the badge follows it for free.
 *
 * The server answers only for a person in the viewer's scope; anyone else gets
 * a 403, so `allowed` stays false and the Tasks button is never offered.
 *
 * A task update in the chat with this person (a status move, new discussion)
 * changes what the badge should say, so one arriving reloads the counts.
 */
const useChatTasks = (personId) => {
    const [counts, setCounts] = useState(null);
    // Drops a slow response for a chat the person has already left.
    const requestId = useRef(0);

    const reload = useCallback(async () => {
        const id = ++requestId.current;
        setCounts(null);
        if (!personId) return;
        try {
            const res = await api.get(`/tasks/with/${personId}`, { params: { countOnly: 1 } });
            if (id === requestId.current) setCounts(res.data.counts);
        } catch (err) {
            // Out of scope (403) or unreachable: no button at all.
        }
    }, [personId]);

    useEffect(() => { reload(); }, [reload]);

    useEffect(() => {
        if (!personId) return undefined;
        let timer = null;
        const onMessage = (msg) => {
            const event = msg?.systemEvent;
            if (event?.type !== 'task_update') return;
            if (idOf(msg.sender) !== String(personId) && idOf(event.targets?.[0]) !== String(personId)) return;
            // The server posts one line per person on the task; one reload.
            clearTimeout(timer);
            timer = setTimeout(reload, 400);
        };
        const offNew = onSocket('message:new', onMessage);
        const offEdited = onSocket('message:edited', onMessage);
        return () => { clearTimeout(timer); offNew(); offEdited(); };
    }, [personId, reload]);

    return {
        allowed: Boolean(counts),
        openCount: counts?.open || 0,
        unreadCount: counts?.unread || 0,
        setCounts,
        reload
    };
};

export default useChatTasks;
